import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type PublicSite = {
  copy?: Record<string, unknown>;
  themeJson?: Record<string, unknown>;
};

// PreviewPage renders the public-facing site for a tenant + locale.
// Reads the tenant's public site config from /api/v1/public/{slug}/site
// (no Authorization header — public endpoint). Falls back to a minimal
// generic shell when the firm hasn't configured copy yet.
export default function PreviewPage() {
  const { tenantSlug = "", locale = "en" } = useParams();
  const [site, setSite] = useState<PublicSite | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setSite(null);
    fetch(`/api/v1/public/${encodeURIComponent(tenantSlug)}/site`)
      .then(async (r) => {
        if (r.status === 404) {
          setSite({});
          return;
        }
        if (!r.ok) throw new Error(`status ${r.status}`);
        setSite((await r.json()) as PublicSite);
      })
      .catch((e) => setError(String(e?.message ?? e)));
  }, [tenantSlug]);

  const headline =
    locale === "es"
      ? "Firma de inmigración"
      : "Practice management for modern law firms";
  const cta =
    locale === "es" ? "Reservá una consulta" : "Book a consultation";

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <header className="mb-10 flex items-baseline justify-between">
        <span className="text-lg font-semibold text-brand-900">juris</span>
        <a href={`/preview/${tenantSlug}/${locale === "es" ? "en" : "es"}`} className="text-sm text-brand-700 hover:underline">
          {locale === "es" ? "English" : "Español"}
        </a>
      </header>
      <h1 className="text-4xl font-bold text-brand-900">{headline}</h1>
      <p className="mt-6 text-slate-600">
        <strong>{tenantSlug}</strong>
        {site === null && !error && " — …"}
        {site && " — public preview"}
      </p>
      {error && <p className="mt-4 text-red-600">{error}</p>}
      <a
        href={`/api/v1/public/${encodeURIComponent(tenantSlug)}/attorneys`}
        className="mt-8 inline-block rounded-md bg-brand-700 px-5 py-3 text-sm font-medium text-white hover:bg-brand-500"
      >
        {cta}
      </a>
      <footer className="mt-16 border-t border-slate-200 pt-6 text-xs text-slate-400">
        <a href={`/preview/${tenantSlug}/robots.txt`} className="hover:underline">
          robots.txt
        </a>
        {" · "}
        <a href={`/preview/${tenantSlug}/sitemap.xml`} className="hover:underline">
          sitemap.xml
        </a>
      </footer>
    </main>
  );
}
